import config from '@/config'
import Groups, { IGroup, IUserGroups } from '@/models/Groups'
import { useGlobalStore } from '@/store/app'
import { waitForStoreIds } from '@/util/storeHelpers'
import { isGokollab } from '@ghl/clientportal-core/init'
import BaseService from './BaseService'
import { ServiceWithStoreDependencies } from './decorators/serviceDecorators'
import { requests } from './HTTPService'
import { IPaginationParams } from './types'

const http = requests(config?.COMMUNITIES_SERVER_URL)

@ServiceWithStoreDependencies('locationId')
class GroupsServiceCls extends BaseService<Groups> {
  constructor() {
    super(null as any)
  }

  model() {
    return Groups
  }

  get endpoint(): string {
    const appStore = useGlobalStore()
    return `/${appStore.locationId}/groups`
  }

  async getGroupBySlug(slug: string) {
    if (
      window.__preloadedApiData?.groupData &&
      window.__preloadedApiData?.groupData?.groupDetail?.slug === slug
    ) {
      return window.__preloadedApiData?.groupData
    }
    const basePath = isGokollab()
      ? `${config.GOKOLLAB_LOCATION_ID}/groups`
      : this.endpoint

    const response = await http.get(`${basePath}/slug/${slug}`)
    return response.data
  }

  async getGroupByLocation(query?: IPaginationParams) {
    const response = await http.get(`${this.endpoint}`, { params: query })
    return response.data
  }

  async updateChannelSequence(
    groupId: string,
    updatedChannelSequence: [
      { id: string; channelName: string; sequence: number }
    ]
  ) {
    const response = await http.put(
      `${this.endpoint}/${groupId}/channels/update-channel-sequence`,
      { updatedChannelSequence }
    )
    return response.data
  }

  async getGroupMembersLevelDistribution() {
    await waitForStoreIds()
    const response = await http.get(
      `${this.endpoint}/${
        useGlobalStore().$state.groupId
      }/users-level-distribution`
    )
    return response.data
  }

  async getPinnedGroups(): Promise<any> {
    try {
      const response = await http.get(`${this.endpoint}/pinned`)
      return response.data
    } catch (_error) {
      return []
    }
  }

  async pinGroup(groupId: string): Promise<any> {
    const response = await http.post(`${this.endpoint}/${groupId}/pin`)
    return response.data
  }

  async unpinGroup(groupId: string): Promise<any> {
    const response = await http.post(`${this.endpoint}/${groupId}/unpin`)
    return response.data
  }

  async syncPricing(groupId: string): Promise<any> {
    const response = await http.put(`${this.endpoint}/${groupId}/sync-pricing`)
    return response.data
  }
  async initiateGroupPayment(
    groupId: string,
    contactId: string,
    orderId: string,
    orderSourceId: string
  ): Promise<any> {
    const response = await http.post(
      `${this.endpoint}/${groupId}/users/${contactId}/initiate-group-payment`,
      {
        orderId,
        orderSourceId,
      }
    )
    return response.data
  }

  async captureGroupPayment(
    groupId: string,
    contactId: string,
    orderId: string,
    orderSourceId: string
  ): Promise<any> {
    const response = await http.post(
      `${this.endpoint}/${groupId}/users/${contactId}/capture-group-payment`,
      {
        orderId,
        orderSourceId,
      }
    )
    return response.data
  }

  async getGroupOwnedByUser(
    contactId: string,
    offset: number,
    limit: number
  ): Promise<{
    groups: Partial<IGroup>[]
    total: number
  }> {
    const response = await http.get(`${this.endpoint}/owned-by/${contactId}`, {
      params: { offset, limit },
    })
    return response.data
  }

  async getGroupOwnedByUserWithPagination(
    contactId: string,
    pageNo: number,
    limit: number
  ): Promise<IUserGroups> {
    const response = await http.get(`${this.endpoint}/owned-by/${contactId}`, {
      params: { pageNo, limit },
    })
    return response.data
  }
}

// Export singleton instance
export const GroupsService = new GroupsServiceCls()

// Export type for dependency injection
export type IGroupsService = GroupsServiceCls

// Export commonly used methods directly
export const {
  getGroupBySlug,
  getGroupByLocation,
  updateChannelSequence,
  getGroupMembersLevelDistribution,
  getPinnedGroups,
  pinGroup,
  unpinGroup,
  getGroupOwnedByUser,
  initiateGroupPayment,
  captureGroupPayment,
} = GroupsService
